import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Activity,
  Watch,
  Smartphone,
  RefreshCw,
  Check,
  X,
  Link2,
  Link2Off,
  Footprints,
  Flame,
  Timer,
  Heart,
  Moon,
  Bike,
  Mountain,
  AlertCircle,
  Zap,
  Crown,
  TrendingUp,
  Battery,
  ThermometerSun,
  Wind,
  CircleDot
} from 'lucide-react';

// Standard Platform configurations
const STANDARD_PLATFORMS = {
  google_fit: {
    name: 'Google Fit',
    icon: Activity,
    color: 'text-green-500',
    bgColor: 'bg-green-500/20',
    description: 'Sync steps, calories, and activity data from Google Fit',
    tier: 'standard'
  },
  fitbit: {
    name: 'Fitbit',
    icon: Watch,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/20',
    description: 'Connect your Fitbit device for comprehensive health tracking',
    tier: 'standard'
  },
  strava: {
    name: 'Strava',
    icon: Bike,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/20',
    description: 'Import runs, rides, and activities from Strava',
    tier: 'standard'
  }
};

// Premium Platform configurations
const PREMIUM_PLATFORMS = {
  whoop: {
    name: 'WHOOP',
    icon: Zap,
    color: 'text-red-500',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    description: 'Recovery, strain, sleep & HRV tracking',
    tier: 'premium',
    features: ['Recovery Score', 'Strain', 'HRV', 'Sleep Performance']
  },
  oura: {
    name: 'Oura Ring',
    icon: CircleDot,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/30',
    description: 'Readiness, sleep quality & activity insights',
    tier: 'premium',
    features: ['Readiness Score', 'Sleep Analysis', 'Activity', 'HRV']
  },
  apple_health: {
    name: 'Apple Watch',
    icon: Watch,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-500/30',
    description: 'HealthKit sync via iOS app (coming soon)',
    tier: 'premium',
    features: ['Heart Rate', 'HRV', 'Workouts', 'Blood Oxygen']
  },
  garmin: {
    name: 'Garmin',
    icon: Mountain,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    description: 'Advanced GPS & performance metrics',
    tier: 'premium',
    features: ['Training Load', 'Body Battery', 'Stress', 'Sleep']
  },
  polar: {
    name: 'Polar',
    icon: Heart,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/20',
    borderColor: 'border-rose-500/30',
    description: 'Heart rate training & recovery tracking',
    tier: 'premium',
    features: ['Training Load Pro', 'Nightly Recharge', 'Running Power']
  }
};

export default function WearablesPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [standardStatuses, setStandardStatuses] = useState({});
  const [premiumStatuses, setPremiumStatuses] = useState({});
  const [syncedData, setSyncedData] = useState({});
  const [premiumData, setPremiumData] = useState({});
  const [activeTab, setActiveTab] = useState('premium');

  // Handle OAuth callbacks
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    
    if (connected) {
      const platformName = PREMIUM_PLATFORMS[connected]?.name || STANDARD_PLATFORMS[connected]?.name || connected;
      toast.success(`${platformName} connected successfully!`);
      window.history.replaceState({}, '', '/wearables');
    }
    
    if (error) {
      toast.error(`Connection failed: ${error}`);
      window.history.replaceState({}, '', '/wearables');
    }
  }, [searchParams]);

  const fetchStandardStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/all/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStandardStatuses(response.data);
    } catch (error) {
      console.error('Error fetching standard statuses:', error);
    }
  }, [token]);

  const fetchPremiumStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/premium-wearables/all/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumStatuses(response.data);
    } catch (error) {
      console.error('Error fetching premium statuses:', error);
    }
  }, [token]);

  const fetchSyncedData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations/all/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncedData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [token]);

  const fetchPremiumData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/premium-wearables/all/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumData(response.data);
    } catch (error) {
      console.error('Error fetching premium data:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStandardStatuses(),
        fetchPremiumStatuses(),
        fetchSyncedData(),
        fetchPremiumData()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchStandardStatuses, fetchPremiumStatuses, fetchSyncedData, fetchPremiumData]);

  const handleConnect = async (platform, isPremium = false) => {
    if (isPremium) {
      if (platform === 'apple_health') {
        toast.info('Apple HealthKit sync will be available in the iOS app');
        return;
      }
      window.location.href = `${API}/premium-wearables/${platform}/auth`;
    } else {
      if (platform === 'google_fit') {
        window.location.href = `${API}/integrations/google-fit/auth`;
      } else {
        window.location.href = `${API}/integrations/${platform}/auth`;
      }
    }
  };

  const handleDisconnect = async (platform, isPremium = false) => {
    const platformConfig = isPremium ? PREMIUM_PLATFORMS[platform] : STANDARD_PLATFORMS[platform];
    if (!window.confirm(`Disconnect ${platformConfig?.name}?`)) return;
    
    try {
      if (isPremium) {
        await axios.delete(`${API}/premium-wearables/${platform}/disconnect`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        if (platform === 'google_fit') {
          await axios.delete(`${API}/integrations/google-fit/revoke`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.delete(`${API}/integrations/${platform}/disconnect`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      toast.success(`${platformConfig?.name} disconnected`);
      isPremium ? fetchPremiumStatuses() : fetchStandardStatuses();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSync = async (platform, isPremium = false) => {
    setSyncing(prev => ({ ...prev, [platform]: true }));
    
    try {
      if (isPremium) {
        await axios.post(`${API}/premium-wearables/${platform}/sync`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
        await fetchPremiumData();
      } else {
        if (platform === 'google_fit') {
          await axios.post(`${API}/integrations/google-fit/sync`, null, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post(`${API}/integrations/${platform}/sync`, null, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        await fetchSyncedData();
      }
      const platformConfig = isPremium ? PREMIUM_PLATFORMS[platform] : STANDARD_PLATFORMS[platform];
      toast.success(`${platformConfig?.name} data synced!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [platform]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate totals
  const totalSteps = syncedData.totals?.steps || 0;
  const totalCalories = syncedData.totals?.calories || 0;
  const totalActiveMinutes = syncedData.totals?.active_minutes || 0;
  
  // Premium metrics
  const recoveryScore = premiumData?.unified_metrics?.average_recovery_score;
  const whoopData = premiumData?.platforms?.whoop;
  const ouraData = premiumData?.platforms?.oura;

  // Count connected devices
  const connectedPremium = Object.values(premiumStatuses).filter(s => s?.connected).length;
  const connectedStandard = Object.values(standardStatuses).filter(s => s?.connected).length;

  return (
    <div data-testid="wearables-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          WEARABLE <span className="text-primary">INTEGRATIONS</span>
        </h1>
        <p className="text-gray-400 mt-1">Connect your fitness devices for comprehensive health tracking</p>
      </div>

      {/* Recovery Overview Card - Only show if premium connected */}
      {recoveryScore && (
        <div className="card-tactical border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                <Battery className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Today's Recovery</h3>
                <p className="text-gray-500 text-sm">Combined from all devices</p>
              </div>
            </div>
            <div className={`text-4xl font-bold ${
              recoveryScore >= 67 ? 'text-green-400' : 
              recoveryScore >= 34 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {Math.round(recoveryScore)}%
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {whoopData?.recovery?.hrv_rmssd_milli && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                  <Heart className="w-3 h-3" />
                  HRV
                </div>
                <div className="text-white font-mono text-lg">
                  {Math.round(whoopData.recovery.hrv_rmssd_milli)} <span className="text-xs text-gray-500">ms</span>
                </div>
              </div>
            )}
            {whoopData?.recovery?.resting_heart_rate && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                  <Activity className="w-3 h-3" />
                  Resting HR
                </div>
                <div className="text-white font-mono text-lg">
                  {Math.round(whoopData.recovery.resting_heart_rate)} <span className="text-xs text-gray-500">bpm</span>
                </div>
              </div>
            )}
            {ouraData?.sleep?.score && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                  <Moon className="w-3 h-3" />
                  Sleep Score
                </div>
                <div className="text-white font-mono text-lg">
                  {ouraData.sleep.score}
                </div>
              </div>
            )}
            {ouraData?.activity?.steps && (
              <div className="bg-secondary/30 rounded-sm p-3">
                <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                  <Footprints className="w-3 h-3" />
                  Steps
                </div>
                <div className="text-white font-mono text-lg">
                  {ouraData.activity.steps.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          onClick={() => setActiveTab('premium')}
          className={`px-4 py-2 rounded-t-sm font-medium transition-colors ${
            activeTab === 'premium' 
              ? 'bg-primary/20 text-primary border-b-2 border-primary' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Crown className="w-4 h-4 inline mr-2" />
          Premium Wearables ({connectedPremium})
        </button>
        <button
          onClick={() => setActiveTab('standard')}
          className={`px-4 py-2 rounded-t-sm font-medium transition-colors ${
            activeTab === 'standard' 
              ? 'bg-primary/20 text-primary border-b-2 border-primary' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Activity className="w-4 h-4 inline mr-2" />
          Standard Apps ({connectedStandard})
        </button>
      </div>

      {/* Premium Wearables Tab */}
      {activeTab === 'premium' && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(PREMIUM_PLATFORMS).map(([key, platform]) => {
              const status = premiumStatuses[key] || {};
              const isConnected = status.connected;
              const isConfigured = status.configured !== false;
              const isSyncing = syncing[key];
              const platformData = premiumData?.platforms?.[key];
              const Icon = platform.icon;

              return (
                <div
                  key={key}
                  data-testid={`premium-wearable-${key}`}
                  className={`card-tactical border ${isConnected ? platform.borderColor : 'border-border'} hover:border-primary/30 transition-colors`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${platform.bgColor} rounded-full flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${platform.color}`} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{platform.name}</h3>
                        <p className="text-gray-500 text-xs">{platform.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Connection Status */}
                  <div className={`mb-3 px-2 py-1 rounded-sm text-xs inline-flex items-center gap-1 ${
                    isConnected ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {isConnected ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {isConnected ? 'Connected' : 'Not Connected'}
                  </div>

                  {/* Features */}
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1">
                      {platform.features.map((feature, idx) => (
                        <span key={idx} className="text-xs bg-secondary/50 text-gray-400 px-2 py-0.5 rounded-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Connection Info */}
                  {isConnected && (
                    <div className="mb-3 p-2 bg-secondary/30 rounded-sm text-xs space-y-1">
                      {status.name && (
                        <p className="text-gray-400">Account: <span className="text-white">{status.name}</span></p>
                      )}
                      {status.email && (
                        <p className="text-gray-400">Email: <span className="text-white">{status.email}</span></p>
                      )}
                      {status.last_sync && (
                        <p className="text-gray-400">Last sync: <span className="text-white">{new Date(status.last_sync).toLocaleString()}</span></p>
                      )}
                    </div>
                  )}

                  {/* Synced Data Preview */}
                  {isConnected && platformData && (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      {key === 'whoop' && platformData.recovery && (
                        <>
                          <div className="text-center p-2 bg-secondary/20 rounded-sm">
                            <div className="text-sm font-mono text-green-400">{platformData.recovery.recovery_score}%</div>
                            <div className="text-xs text-gray-500">Recovery</div>
                          </div>
                          {platformData.strain && (
                            <div className="text-center p-2 bg-secondary/20 rounded-sm">
                              <div className="text-sm font-mono text-orange-400">{platformData.strain.strain?.toFixed(1)}</div>
                              <div className="text-xs text-gray-500">Strain</div>
                            </div>
                          )}
                        </>
                      )}
                      {key === 'oura' && (
                        <>
                          {platformData.readiness && (
                            <div className="text-center p-2 bg-secondary/20 rounded-sm">
                              <div className="text-sm font-mono text-purple-400">{platformData.readiness.score}</div>
                              <div className="text-xs text-gray-500">Readiness</div>
                            </div>
                          )}
                          {platformData.sleep && (
                            <div className="text-center p-2 bg-secondary/20 rounded-sm">
                              <div className="text-sm font-mono text-blue-400">{platformData.sleep.score}</div>
                              <div className="text-xs text-gray-500">Sleep</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Not Configured Warning */}
                  {!isConfigured && key !== 'apple_health' && (
                    <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                      <div className="flex items-center gap-2 text-yellow-400 text-xs">
                        <AlertCircle className="w-3 h-3" />
                        <span>API credentials not configured</span>
                      </div>
                    </div>
                  )}

                  {/* Apple Health / Garmin Special Messages */}
                  {key === 'apple_health' && (
                    <div className="mb-3 p-2 bg-pink-500/10 border border-pink-500/30 rounded-sm">
                      <p className="text-xs text-pink-400">Sync via iOS app (coming soon)</p>
                      <p className="text-xs text-gray-500 mt-1">HealthKit data will sync automatically</p>
                    </div>
                  )}

                  {key === 'garmin' && !status.configured && (
                    <div className="mb-3 p-2 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                      <p className="text-xs text-blue-400">Awaiting API approval</p>
                      <p className="text-xs text-gray-500 mt-1">Garmin Connect API access pending</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {isConnected ? (
                      <>
                        <Button
                          onClick={() => handleSync(key, true)}
                          disabled={isSyncing}
                          className="flex-1 bg-primary hover:bg-red-700 text-sm"
                          size="sm"
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                          {isSyncing ? 'Syncing...' : 'Sync'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDisconnect(key, true)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          size="sm"
                        >
                          <Link2Off className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnect(key, true)}
                        disabled={!isConfigured || key === 'apple_health'}
                        className="w-full bg-secondary hover:bg-secondary/80 text-sm"
                        size="sm"
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        {key === 'apple_health' ? 'Coming Soon' : 
                         key === 'garmin' && !isConfigured ? 'Pending Approval' : 
                         `Connect ${platform.name}`}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Standard Apps Tab */}
      {activeTab === 'standard' && (
        <div className="space-y-4">
          {/* Aggregated Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-tactical text-center">
              <Footprints className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <div className="font-mono text-2xl text-white">{totalSteps.toLocaleString()}</div>
              <div className="text-xs text-gray-400">Total Steps</div>
            </div>
            <div className="card-tactical text-center">
              <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <div className="font-mono text-2xl text-white">{totalCalories.toLocaleString()}</div>
              <div className="text-xs text-gray-400">Calories Burned</div>
            </div>
            <div className="card-tactical text-center">
              <Timer className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <div className="font-mono text-2xl text-white">{totalActiveMinutes}</div>
              <div className="text-xs text-gray-400">Active Minutes</div>
            </div>
            <div className="card-tactical text-center">
              <Activity className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <div className="font-mono text-2xl text-white">{connectedStandard}</div>
              <div className="text-xs text-gray-400">Connected</div>
            </div>
          </div>

          {/* Platform Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(STANDARD_PLATFORMS).map(([key, platform]) => {
              const status = standardStatuses[key] || {};
              const isConnected = status.connected;
              const isConfigured = status.configured !== false;
              const isSyncing = syncing[key];
              const platformData = syncedData.platforms?.[key];
              const Icon = platform.icon;

              return (
                <div
                  key={key}
                  data-testid={`standard-wearable-${key}`}
                  className={`card-tactical border ${isConnected ? 'border-green-500/30' : 'border-border'}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 ${platform.bgColor} rounded-full flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${platform.color}`} />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{platform.name}</h3>
                        <p className="text-gray-500 text-sm">{platform.description}</p>
                      </div>
                    </div>
                    
                    {isConnected ? (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <Check className="w-4 h-4" />
                        Connected
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-500 text-sm">
                        <X className="w-4 h-4" />
                        Not Connected
                      </span>
                    )}
                  </div>

                  {/* Connection Info */}
                  {isConnected && (
                    <div className="mb-4 p-3 bg-secondary/30 rounded-sm">
                      {status.athlete_name && (
                        <p className="text-sm text-gray-400">
                          Account: <span className="text-white">{status.athlete_name}</span>
                        </p>
                      )}
                      {status.last_sync && (
                        <p className="text-sm text-gray-400">
                          Last synced: <span className="text-white">{new Date(status.last_sync).toLocaleString()}</span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Platform-specific Data */}
                  {isConnected && platformData && (
                    <div className="mb-4 grid grid-cols-3 gap-2">
                      {platformData.steps !== undefined && (
                        <div className="text-center p-2 bg-secondary/20 rounded-sm">
                          <div className="text-lg font-mono text-white">{platformData.steps?.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">Steps</div>
                        </div>
                      )}
                      {platformData.calories !== undefined && (
                        <div className="text-center p-2 bg-secondary/20 rounded-sm">
                          <div className="text-lg font-mono text-white">{platformData.calories?.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">Cal</div>
                        </div>
                      )}
                      {platformData.active_minutes !== undefined && (
                        <div className="text-center p-2 bg-secondary/20 rounded-sm">
                          <div className="text-lg font-mono text-white">{platformData.active_minutes}</div>
                          <div className="text-xs text-gray-500">Active</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Not Configured Warning */}
                  {!isConfigured && (
                    <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>API credentials not configured</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Contact your admin to set up {platform.name} integration
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {isConnected ? (
                      <>
                        <Button
                          onClick={() => handleSync(key, false)}
                          disabled={isSyncing}
                          className="flex-1 bg-primary hover:bg-red-700"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                          {isSyncing ? 'Syncing...' : 'Sync Now'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDisconnect(key, false)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        >
                          <Link2Off className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleConnect(key, false)}
                        disabled={!isConfigured}
                        className="w-full bg-secondary hover:bg-secondary/80"
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Connect {platform.name}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="card-tactical">
        <h3 className="font-heading text-lg text-white uppercase mb-3">About Wearable Integrations</h3>
        <div className="text-gray-400 text-sm space-y-2">
          <p>
            <strong className="text-primary">Premium Wearables:</strong> Connect high-accuracy devices like WHOOP, Oura Ring, 
            Apple Watch, and Garmin for advanced biometrics including HRV, recovery scores, sleep analysis, and strain tracking.
          </p>
          <p>
            <strong className="text-white">Standard Apps:</strong> Sync basic activity data from Google Fit, Fitbit, and Strava 
            including steps, calories, and workout activities.
          </p>
          <p className="text-xs text-gray-500">
            Note: Some integrations require API credentials to be configured. Premium wearables may require device ownership 
            for API access. Data is synced manually - click "Sync" to get the latest data.
          </p>
        </div>
      </div>
    </div>
  );
}
